import { motion } from 'framer-motion';
import { useRef, useState } from 'react';
import ReCAPTCHA from 'react-google-recaptcha';
import { PaperAirplaneIcon } from '@heroicons/react/24/outline';

const ContactForm = () => {
  const [formStatus, setFormStatus] = useState<'idle' | 'loading' | 'success' | 'error'>('idle');
  const [captchaValue, setCaptchaValue] = useState<string | null>(null);
  const [formData, setFormData] = useState({
    name: '',
    email: '',
    company: '',
    description: ''
  });
  const formRef = useRef<HTMLFormElement>(null);
  const recaptchaRef = useRef<ReCAPTCHA>(null);

  const handleInputChange = (e: React.ChangeEvent<HTMLInputElement | HTMLTextAreaElement>) => {
    const { name, value } = e.target;
    setFormData(prev => ({
      ...prev,
      [name]: value
    }));
  };

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    
    if (!captchaValue) {
      alert('Please verify that you are not a robot');
      return;
    }

    setFormStatus('loading');

    try {
      const response = await fetch('https://eosllhlwamcvb5h.m.pipedream.net', {
        method: 'POST',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify({
          token: captchaValue,
          name: formData.name,
          email: formData.email,
          organization: formData.company,
          message: formData.description
        }),
      });
      
      const data = await response.json();
      
      if (!response.ok || !data.success) {
        throw new Error(data.message || 'Failed to send message');
      }

      setFormStatus('success');
      setFormData({
        name: '',
        email: '',
        company: '',
        description: ''
      });
      recaptchaRef.current?.reset();
      setCaptchaValue(null);
    } catch (error) {
      setFormStatus('error');
      console.error('Failed to send message:', error);
    }
  };

  return (
    <motion.div
      initial={{ opacity: 0, y: 20 }}
      whileInView={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5, delay: 0.2 }}
      className="max-w-2xl mx-auto"
    >
      <div className="relative group">
        <div className="absolute inset-0 bg-gradient-to-br from-indigo-900/30 to-purple-900/30 rounded-lg backdrop-blur-sm border border-indigo-500/20 
          group-hover:border-indigo-500/40 transition-all duration-300" />
        
        <form ref={formRef} onSubmit={handleSubmit} className="relative p-8 space-y-6">
          <div>
            <label htmlFor="name" className="block text-sm font-medium text-slate-300 mb-2 font-mono">
              Identifier
            </label>
            <input
              type="text"
              id="name"
              name="name"
              value={formData.name}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent 
                transition-colors text-white placeholder-slate-400 font-mono"
              placeholder="Your identifier"
            />
          </div>

          <div>
            <label htmlFor="email" className="block text-sm font-medium text-slate-300 mb-2 font-mono">
              Communication Channel
            </label>
            <input
              type="email"
              id="email"
              name="email"
              value={formData.email}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent 
                transition-colors text-white placeholder-slate-400 font-mono"
              placeholder="secure@domain.com"
            />
          </div>

          <div>
            <label htmlFor="company" className="block text-sm font-medium text-slate-300 mb-2 font-mono">
              Organization
            </label>
            <input
              type="text"
              id="company"
              name="company"
              value={formData.company}
              onChange={handleInputChange}
              required
              className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent 
                transition-colors text-white placeholder-slate-400 font-mono"
              placeholder="Organization name"
            />
          </div>

          <div>
            <label htmlFor="description" className="block text-sm font-medium text-slate-300 mb-2 font-mono">
              Message Content
            </label>
            <textarea
              id="description"
              name="description"
              value={formData.description}
              onChange={handleInputChange}
              required
              rows={4}
              className="w-full px-4 py-2 bg-slate-800/50 border border-slate-700/50 rounded-lg focus:ring-2 focus:ring-indigo-500 focus:border-transparent 
                transition-colors text-white placeholder-slate-400 font-mono resize-none"
              placeholder="Describe your requirements..."
            />
          </div>

          <div className="flex justify-center">
            <ReCAPTCHA
              ref={recaptchaRef}
              sitekey="6Ld7JNoqAAAAAKDs4F3bxhHAoRVIqHi9dSHHoS5M"
              onChange={(value) => setCaptchaValue(value)}
              theme="dark"
            />
          </div>

          <div className="flex justify-center">
            <motion.button
              type="submit"
              disabled={formStatus === 'loading'}
              whileHover={{ scale: 1.05 }}
              whileTap={{ scale: 0.95 }}
              className={`
                relative group inline-flex items-center px-8 py-3 rounded-lg text-white font-semibold
                ${formStatus === 'loading' 
                  ? 'bg-slate-600 cursor-not-allowed' 
                  : 'bg-indigo-600 hover:bg-indigo-700'}
                transition-colors font-mono
              `}
            >
              {formStatus === 'loading' ? (
                <>
                  <svg className="animate-spin -ml-1 mr-3 h-5 w-5 text-white" xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
                    <circle className="opacity-25" cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="4"></circle>
                    <path className="opacity-75" fill="currentColor" d="M4 12a8 8 0 018-8V0C5.373 0 0 5.373 0 12h4zm2 5.291A7.962 7.962 0 014 12H0c0 3.042 1.135 5.824 3 7.938l3-2.647z"></path>
                  </svg>
                  Processing...
                </>
              ) : (
                <>
                  <PaperAirplaneIcon className="w-5 h-5 mr-2" />
                  Transmit Message
                  <span className="ml-2 text-xs opacity-60">[SECURE]</span>
                </>
              )}
            </motion.button>
          </div>

          {formStatus === 'success' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-green-400 text-center font-mono"
            >
              Transmission successful. Expect response within 24 hours.
            </motion.div>
          )}

          {formStatus === 'error' && (
            <motion.div
              initial={{ opacity: 0 }}
              animate={{ opacity: 1 }}
              className="text-red-400 text-center font-mono"
            >
              Transmission failed. Please try again.
            </motion.div>
          )}

          {/* Corner decorations */}
          <div className="absolute top-0 right-0 w-8 h-8 border-t border-r border-indigo-500/20 rounded-tr-lg" />
          <div className="absolute bottom-0 left-0 w-8 h-8 border-b border-l border-indigo-500/20 rounded-bl-lg" />
        </form>
      </div>
    </motion.div>
  );
};

export default ContactForm;